<template>
  <div class="countdown" :class="[`countdown--${variant}`, `countdown--${theme}`]">
    <div class="countdown__block" v-show="isCountdownAboveDay">
      <div class="countdown__number">
        {{ days }}
      </div>
      <div class="countdown__unit">
        {{ $tc(`${variantKey}.days`, days) }}
      </div>
    </div>
    <div class="countdown__block">
      <div class="countdown__number">
        {{ hours }}
      </div>
      <div class="countdown__unit">
        {{ $tc(`${variantKey}.hours`, hours) }}
      </div>
    </div>
    <div class="countdown__block">
      <div class="countdown__number">
        {{ minutes }}
      </div>
      <div class="countdown__unit">
        {{ $tc(`${variantKey}.minutes`, minutes) }}
      </div>
    </div>
    <div class="countdown__block">
      <div class="countdown__number">
        {{ seconds }}
      </div>
      <div class="countdown__unit">
        {{ $tc(`${variantKey}.seconds`, seconds) }}
      </div>
    </div>
  </div>
</template>

<script>
import { SECOND_MS, MINUTE_MS, HOUR_MS, DAY_MS } from '@/helpers/time'

export const VARIANTS = {
  large: 'large',
  medium: 'medium',
  small: 'small',
}

export const THEMES = {
  black: 'black',
  white: 'white',
}

export default {
  name: 'Countdown',
  props: {
    endDate: {
      type: String,
      required: true,
    },
    variant: {
      type: String,
      default: VARIANTS.large,
      validator(value) {
        return Object.values(VARIANTS).includes(value)
      },
    },
    theme: {
      type: String,
      default: THEMES.white,
      validator(value) {
        return Object.values(THEMES).includes(value)
      },
    },
  },

  data() {
    return {
      interval: -1,
      isCountdownAboveDay: true,
      days: 0,
      minutes: 0,
      hours: 0,
      seconds: 0,
    }
  },

  computed: {
    variantKey() {
      return this.variant === VARIANTS.medium ? VARIANTS.small : this.variant
    },
  },

  watch: {
    endDate: {
      immediate: true,
      handler() {
        this.timerCount()
        this.interval = setInterval(this.timerCount, 1000)
      },
    },
  },

  methods: {
    timerCount() {
      const now = new Date().getTime()
      const endTime = Date.parse(this.endDate)
      const timeLeft = endTime - now
      this.isCountdownAboveDay = timeLeft >= DAY_MS
      if (timeLeft < 0) {
        clearInterval(this.interval)
      } else {
        this.days = Math.floor(timeLeft / DAY_MS)
        this.hours = Math.floor((timeLeft % DAY_MS) / HOUR_MS)
        this.minutes = Math.floor((timeLeft % HOUR_MS) / MINUTE_MS)
        this.seconds = Math.floor((timeLeft % MINUTE_MS) / SECOND_MS)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.countdown {
  display: flex;
  justify-content: center;

  &__block {
    text-align: center;

    .countdown--large & {
      margin: 0 8px;

      @media ($mq-larger) {
        margin: 0 14px;
      }
    }

    .countdown--medium & {
      margin: 0 8px;
    }

    .countdown--small & {
      margin: 0 2px;
    }
  }

  &__number {
    margin: 0 auto;
    font-weight: bold;
    text-align: center;

    .countdown--large & {
      padding: 0 8px 0 2px;
      font-size: $font-size-heading-normal;

      @media ($mq-larger) {
        font-size: $font-size-heading-large;
      }
    }

    .countdown--medium & {
      width: 33px;
      font-size: $font-size-heading-smallest;
      line-height: $font-size-heading-small;
      letter-spacing: 1px;
    }

    .countdown--small & {
      width: 30px;
      height: 30px;
      padding: 10px 8px;
      font-size: $font-size-smaller;
      line-height: $font-size-smaller;
      letter-spacing: 1px;
    }

    .countdown--white & {
      color: $color-white;
    }

    .countdown--black & {
      color: $color-black;
      border-radius: 1px;
    }
  }

  &__unit {
    color: $color-grey-mid-2;
    text-transform: uppercase;
    text-align: center;

    .countdown--large & {
      margin-top: 0;
      font-size: 8px;
      font-weight: 700;
      letter-spacing: 2px;

      @media ($mq-larger) {
        font-size: $font-size-xsmall;
      }
    }

    .countdown--medium & {
      font-size: $font-size-xsmall;
      letter-spacing: 0.15em;
      font-weight: bold;
    }

    .countdown--small & {
      margin-top: 5px;
      font-size: 8px;
    }
  }
}
</style>

<i18n src="@/locales/countdown.json">
</i18n>
